import { html, render, TemplateResult } from 'lit'
import { Note } from '@koblime/common'
import { customElement, property } from 'lit/decorators.js'
import { inject, Symbols } from '../di'
import { MeStore, NotesStore } from '../stores'
import { View } from './view'

/**
 * What's new — opens on the first unseen announcement, or the newest when the
 * account is caught up; prev/next walks the history a note at a time, so the
 * list is never fetched whole. Fixed size: the body scrolls rather than
 * resizing the dialog between notes. Closing remembers the newest note shown.
 */
// Parsed as local so YYYY-MM-DD never shifts a day across timezones. A note
// missing its date must degrade to no date, never break the dialog.
const noteDate = (note: Note): string => {
    const [y, m, d] = (note.date ?? '').split('-').map(Number)
    if (!y || !m || !d) return ''
    return new Date(y, m - 1, d).toLocaleDateString(undefined,
        { year: 'numeric', month: 'long', day: 'numeric' })
}

// Not markdown, just its two habits notes actually use: a line is a paragraph,
// lines starting with `* ` or `- ` group into a bullet list. Lit escapes the text.
const noteBody = (body: string): TemplateResult[] => {
    const blocks: TemplateResult[] = []
    let bullets: string[] = []
    const flush = () => {
        if (bullets.length)
            blocks.push(html`<ul style="margin: 0;">${bullets.map(b => html`<li>${b}</li>`)}</ul>`)
        bullets = []
    }
    for (const line of body.split('\n').map(l => l.trim())) {
        if (line.startsWith('* ') || line.startsWith('- ')) bullets.push(line.slice(2))
        else {
            flush()
            if (line) blocks.push(html`<p style="margin: var(--lumo-space-s) 0 0 0;">${line}</p>`)
        }
    }
    flush()
    return blocks
}

@customElement('kob-notes')
export class NotesView extends View {

    @inject(Symbols.stores.me)
    meStore: MeStore

    @inject(Symbols.stores.notes)
    notesStore: NotesStore

    @property()
    dialogOpened: boolean

    private seenBefore: string
    private newestShown: string
    private root: HTMLElement

    open = async () => {
        this.seenBefore = this.meStore.lastSeenNoteId
        await this.notesStore.openAt(this.seenBefore)
        if (!this.notesStore.current) return

        this.remember()
        this.dialogOpened = true
    }

    private remember = () => {
        const id = this.notesStore.current?.id
        if (id && (!this.newestShown || id > this.newestShown))
            this.newestShown = id
    }

    private rate = async (score: number) => {
        await this.meStore.rate(score)
        if (this.root)
            this.renderContent(this.root)
    }

    private step = (move: () => Promise<void>) => async () => {
        await move()
        this.remember()
        if (this.root)
            this.renderContent(this.root)
    }

    private handleOpenedChanged = async (e: CustomEvent) => {
        this.dialogOpened = e.detail.value
        if (e.detail.value) return

        const shown = this.newestShown
        this.newestShown = undefined
        this.notesStore.reset()
        if (shown)
            await this.meStore.markNoteSeen(shown)
    }

    render() {
        return html`
            <vaadin-dialog
                aria-label="What's new"
                .opened="${this.dialogOpened}"
                @opened-changed="${this.handleOpenedChanged}"
                .renderer="${this.renderContent}"
            ></vaadin-dialog>
        `
    }

    private renderContent = (root: HTMLElement) => {
        this.root = root

        const store = this.notesStore
        const note = store.current
        if (!note) return

        const isUnseen = !this.seenBefore || note.id > this.seenBefore

        render(html`
            <vaadin-vertical-layout
                theme="spacing"
                style="width: 700px; max-width: 100%; align-items: stretch;"
            >
                <vaadin-horizontal-layout theme="spacing" style="align-items: baseline; justify-content: space-between;">
                    <vaadin-horizontal-layout theme="spacing" style="align-items: center;">
                        <h3 style="margin: var(--lumo-space-m) 0 0 0;">${note.title}</h3>
                        ${isUnseen ? html`<span theme="badge info small">New</span>` : ''}
                    </vaadin-horizontal-layout>
                    <span style="color: var(--lumo-secondary-text-color); font-size: var(--lumo-font-size-xxs);"
                        >${noteDate(note)}</span>
                </vaadin-horizontal-layout>
                <vaadin-vertical-layout
                    style="align-items: stretch; height: 240px; overflow-y: auto;"
                >
                    ${noteBody(note.body)}
                    ${note.link ?
                        html`<p><a href="${note.link}" target="_blank">Learn more</a></p>` : ''
                    }
                </vaadin-vertical-layout>
                <div style="
                    padding-top: var(--lumo-space-s);
                    border-top: 1px solid var(--lumo-contrast-10pct);
                    color: var(--lumo-secondary-text-color);
                    font-size: var(--lumo-font-size-xxs);
                ">
                    <p style="margin: 0 0 var(--lumo-space-s) 0;">
                        Something broken or not quite right? Email me at
                        <a href="mailto:hello@kobli.me">hello@kobli.me</a>.
                        It reaches me directly and I read every one.
                    </p>
                    <p style="margin: 0 0 var(--lumo-space-s) 0;">
                        Koblime is a small thing made by one person for people who love
                        their e-readers. A handful of readers chip in each month; my day
                        job quietly covers the rest. Small and steady is what helps most
                        — a few dollars a month, every month, helps keep things running
                        smoothly and plan ahead.
                    </p>
                    <p style="margin: 0 0 var(--lumo-space-s) 0;">
                        How's Koblime for you?
                        ${[1, 2, 3, 4, 5].map(n => html`<vaadin-icon
                            icon="${(this.meStore.rating ?? 0) >= n ? 'vaadin:star' : 'vaadin:star-o'}"
                            role="button"
                            tabindex="0"
                            aria-label="Rate ${n} of 5"
                            style="width: 13px; height: 13px; cursor: pointer; vertical-align: middle; margin-top: -2px; color: var(--lumo-primary-color);"
                            @click="${() => this.rate(n)}"
                        ></vaadin-icon>`)}
                        ${this.meStore.ratedAt ? html`<span
                            >&nbsp;${this.meStore.ratedAt.toLocaleDateString(undefined,
                                { year: 'numeric', month: 'long', day: 'numeric' })}</span>` : ''}
                    </p>
                    <p style="margin: 0;">
                        <a href="https://www.buymeacoffee.com/koblime/membership" target="_blank"
                            >Become a member</a>
                        &nbsp;— Thank you and happy reading! Narayan
                    </p>
                </div>
                <vaadin-horizontal-layout theme="spacing" style="justify-content: space-between; align-items: center;">
                    <vaadin-horizontal-layout theme="spacing" style="align-items: center;">
                        <vaadin-button
                            theme="tertiary small"
                            aria-label="Previous"
                            ?disabled="${!store.hasOlder}"
                            @click="${this.step(() => store.older())}"
                        >
                            <vaadin-icon icon="vaadin:angle-left"></vaadin-icon>
                        </vaadin-button>
                        <span style="color: var(--lumo-secondary-text-color); font-size: var(--lumo-font-size-s);"
                            >${store.position} of ${store.total}</span>
                        <vaadin-button
                            theme="tertiary small"
                            aria-label="Next"
                            ?disabled="${!store.hasNewer}"
                            @click="${this.step(() => store.newer())}"
                        >
                            <vaadin-icon icon="vaadin:angle-right"></vaadin-icon>
                        </vaadin-button>
                    </vaadin-horizontal-layout>
                    <vaadin-button theme="primary" @click="${() => this.dialogOpened = false}">
                        Done
                    </vaadin-button>
                </vaadin-horizontal-layout>
            </vaadin-vertical-layout>
        `, root)
    }
}
